// src/pages/BranchManagementPage.js
import React, { useEffect, useState } from "react";
import {
    Container,
    Typography,
    Paper,
    Box,
    Button,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Avatar,
    InputAdornment,
    Tooltip,
    IconButton,
    CircularProgress,
    Divider,
    TablePagination,
    Card,
    CardContent,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
    Add,
    Business,
    LocationOn,
    Phone,
    Email,
    Search,
    Visibility,
    Edit,
    Delete,
    CheckCircle,
    ArrowBack,
    Store,
    Warehouse as WarehouseIcon,
} from "@mui/icons-material";
import { MenuItem } from "@mui/material";

import { branchesApi } from "../services/api";
import { toast } from "react-toastify";
import { useTranslation } from 'react-i18next';

export default function BranchManagementPage() {
    const theme = useTheme();
    const { t } = useTranslation();

    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [formData, setFormData] = useState({
        name: "",
        address: "",
        phone: "",
        email: "",
        status: "active",
        type: "pharmacy",
        parentBranchId: "",
    });

    const fetchBranches = async () => {
        setLoading(true);
        setError("");
        try {
            const response = await branchesApi.getAll();
            setBranches(response.data.data || []);
        } catch (err) {
            console.error("Error fetching branches:", err);
            setError(t('branch.failedToLoad'));
            toast.error(`❌ ${t('branch.failedToLoad')}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBranches();
    }, []);

    const filteredBranches = branches.filter((branch) => {
        const term = searchTerm.toLowerCase();
        return (
            branch.name.toLowerCase().includes(term) ||
            branch.address.toLowerCase().includes(term) ||
            (branch.email && branch.email.toLowerCase().includes(term))
        );
    });

    // ✅ Reset pagination when filters change
    useEffect(() => {
        setPage(0);
    }, [searchTerm, branches]);

    // ✅ Paginated list
    const paginatedBranches = filteredBranches.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenCreateModal = () => {
        setSelectedBranch(null);
        setFormData({ name: "", address: "", phone: "", email: "", status: "active", type: "pharmacy", parentBranchId: "" });
        setOpenModal(true);
    };

    const handleOpenEditModal = (branch) => {
        setSelectedBranch(branch);
        setFormData({
            name: branch.name,
            address: branch.address,
            phone: branch.phone || "",
            email: branch.email || "",
            status: branch.status || "active",
            type: branch.type || "pharmacy",
            parentBranchId: branch.parentBranchId || "",
        });
        setOpenModal(true);
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async () => {
        if (!formData.name.trim() || !formData.address.trim()) {
            toast.error(t('branch.nameAndAddressRequired'));
            return;
        }

        const payload = {
            ...formData,
            location: formData.address, // API uses 'location'
            parentBranchId: formData.parentBranchId || null
        };
        delete payload.address; // Remove frontend-specific fields
        delete payload.email; // Email is not in the db model

        try {
            if (selectedBranch) {
                await branchesApi.update(selectedBranch.id, payload);
                toast.success(t('branch.updatedSuccessfully'));
            } else {
                await branchesApi.create(payload);
                toast.success(t('branch.createdSuccessfully'));
            }
            fetchBranches();
            setOpenModal(false);
        } catch (err) {
            const msg = err.response?.data?.message || t('branch.failedToSave');
            toast.error(`❌ ${msg}`);
        }
    };

    const handleDeleteBranch = async (id) => {
        if (!window.confirm(t('branch.confirmDelete'))) return;

        try {
            await branchesApi.delete(id);
            toast.success(t('branch.deletedSuccessfully'));
            fetchBranches();
        } catch (err) {
            const msg = err.response?.data?.message || t('branch.failedToDelete');
            toast.error(`❌ ${msg}`);
        }
    };

    if (loading && branches.length === 0) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
            {/* Header Section */}
            <Box
                sx={{
                    textAlign: "center",
                    mb: 4,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    borderRadius: 4,
                    py: 4,
                    px: 3,
                    color: 'white',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                }}
            >
                <Store sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                    {t('branch.management')}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    {t('branch.manageDescription')}
                </Typography>
            </Box>

            {/* Stats Summary */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                        <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', mb: 2, mx: 'auto' }}>
                                <Business />
                            </Avatar>
                            <Typography variant="h4" fontWeight="bold" color="primary">
                                {branches.length}
                            </Typography>
                            <Typography variant="body1" color="textSecondary">
                                {t('branch.totalBranches')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                        <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main', mb: 2, mx: 'auto' }}>
                                <CheckCircle />
                            </Avatar>
                            <Typography variant="h4" fontWeight="bold" color="success.main">
                                {branches.filter(b => b.status === 'active').length}
                            </Typography>
                            <Typography variant="body1" color="textSecondary">
                                {t('branch.activeBranches')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Actions & Filters */}
            <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={3} alignItems="center">
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                label={t('branch.searchBranches')}
                                fullWidth
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder={t('branch.searchPlaceholder')}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }} sx={{ textAlign: 'right' }}>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={handleOpenCreateModal}
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                                    boxShadow: '0 3px 15px rgba(25, 118, 210, 0.3)',
                                }}
                            >
                                {t('branch.addBranch')}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Table Section */}
            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    overflowX: 'auto', // ✅ Allow horizontal scroll
                    '&::-webkit-scrollbar': { height: 8 },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                        borderRadius: 4
                    }
                }}
            >
                <Table sx={{ minWidth: 900 }}>
                    <TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                        <TableRow>
                            <TableCell><strong>{t('branch.name')}</strong></TableCell>
                            <TableCell><strong>{t('branch.address')}</strong></TableCell>
                            <TableCell><strong>{t('branch.contact')}</strong></TableCell>
                            <TableCell><strong>{t('branch.status')}</strong></TableCell>
                            <TableCell><strong>{t('branch.type')}</strong></TableCell>
                            <TableCell align="center"><strong>{t('common.actions')}</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredBranches
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((branch) => (
                                <TableRow key={branch.id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}>
                                                <Business size="small" />
                                            </Avatar>
                                            <Typography variant="body1" fontWeight="medium">
                                                {branch.name}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <LocationOn sx={{ color: 'text.secondary', fontSize: 18 }} />
                                            <Typography variant="body2">{branch.address}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            {branch.phone && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Phone sx={{ color: 'text.secondary', fontSize: 16 }} />
                                                    <Typography variant="caption">{branch.phone}</Typography>
                                                </Box>
                                            )}
                                            {branch.email && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Email sx={{ color: 'text.secondary', fontSize: 16 }} />
                                                    <Typography variant="caption">{branch.email}</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={branch.status === 'active' ? t('directive.active') : t('directive.inactive')}
                                            color={branch.status === 'active' ? 'success' : 'error'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={branch.type === 'warehouse' ? t('branch.warehouse') : t('branch.pharmacy')}
                                            color={branch.type === 'warehouse' ? 'info' : 'default'}
                                            size="small"
                                            variant="filled"
                                            icon={branch.type === 'warehouse' ? <WarehouseIcon sx={{ fontSize: '14px !important' }} /> : <Store sx={{ fontSize: '14px !important' }} />}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                                            <Tooltip title={t('common.edit')}>
                                                <IconButton
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => handleOpenEditModal(branch)}
                                                    sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) } }}
                                                >
                                                    <Edit />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t('common.delete')}>
                                                <IconButton
                                                    color="error"
                                                    size="small"
                                                    onClick={() => handleDeleteBranch(branch.id)}
                                                    sx={{ '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) } }}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        {filteredBranches.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                    <Typography variant="h6" color="textSecondary">
                                        {t('branch.noBranchesFound')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={filteredBranches.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage={t('common.rowsPerPage')}
                />
            </TableContainer>

            {/* Create/Edit Modal */}
            <Dialog
                open={openModal}
                onClose={() => setOpenModal(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 3,
                    background: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.05)}, transparent)`,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`
                }}>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}>
                        {selectedBranch ? <Edit color="primary" /> : <Add color="primary" />}
                    </Avatar>
                    <Typography variant="h6" component="div" fontWeight="bold">
                        {selectedBranch ? t('branch.editBranch') : t('branch.newBranch')}
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={3} sx={{ mt: 0 }}>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                name="name"
                                label={t('branch.branchName')}
                                fullWidth
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Business color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                name="address"
                                label={t('branch.address')}
                                fullWidth
                                value={formData.address}
                                onChange={handleInputChange}
                                required
                                multiline
                                rows={2}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LocationOn color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                name="phone"
                                label={t('branch.phone')}
                                fullWidth
                                value={formData.phone}
                                onChange={handleInputChange}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Phone color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                name="email"
                                label={t('branch.email')}
                                type="email"
                                fullWidth
                                value={formData.email}
                                onChange={handleInputChange}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                select
                                name="type"
                                label={t('branch.type')}
                                fullWidth
                                value={formData.type}
                                onChange={handleInputChange}
                                InputLabelProps={{ shrink: true }}
                            >
                                <MenuItem value="pharmacy">{t('branch.pharmacy')}</MenuItem>
                                <MenuItem value="warehouse">{t('branch.warehouse')}</MenuItem>
                            </TextField>
                        </Grid>
                        {formData.type === 'pharmacy' && (
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    select
                                    name="parentBranchId"
                                    label={t('branch.assignedWarehouse')}
                                    fullWidth
                                    value={formData.parentBranchId}
                                    onChange={handleInputChange}
                                    InputLabelProps={{ shrink: true }}
                                    helperText={t('branch.assignedWarehouseHelper')}
                                >
                                    <MenuItem value=""><em>{t('common.none') || 'None'}</em></MenuItem>
                                    {branches.filter(b => b.type === 'warehouse').map(w => (
                                        <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 3, gap: 2 }}>
                    <Button
                        onClick={() => setOpenModal(false)}
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        sx={{ borderRadius: 2, px: 3 }}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        startIcon={<CheckCircle />}
                        sx={{
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                            boxShadow: '0 3px 15px rgba(25, 118, 210, 0.3)',
                        }}
                    >
                        {selectedBranch ? t('common.update') : t('common.create')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
